import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Box, Container, Grid, Tab, Tabs, Typography } from "@mui/material";

import { AuthContext } from "../../../components/user/BCPAuthProvider";
import {
  findGetParameter,
  getData,
  newPath,
} from "../../../components/utilities/UtilityFunctions";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import TabPanel, {
  tabPanelA11yProps,
} from "../../../components/layout/TabPanel";

import AccountDetailsTab from "./components/AccountDetailsTab";
import NotificationsTab from "./components/NotificationsTab";
import ProfileTab from "./components/ProfileTab";
import BankDetailsTab from "./components/BankDetailsTab";
import SubscriptionsTab from "./components/SubscriptionsTab";
import OrganizationsTab from "./components/OrganizationsTab";
import TeamsTab from "./components/TeamsTab";
import DisplayTab from "./components/DisaplyTab";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  useProcessing,
  useSignout,
} from "../../../components/user/BCPAuthReducers";
import { signout } from "../../../components/user/BCPAuth";
import { isAppMode } from "../../../global/AppOverlay";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import IdentitiesTab from "./components/IdentitiesTab";
import { stringToUrl } from "../../events/utils/event";

const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";
const IS_BG =
  getEnvironmentVariable("IS_BG") === "true" ||
  getEnvironmentVariable("COMPANY_FLAG") === "bananagrams";
const IS_SOLFORGE = getEnvironmentVariable("IS_SOLFORGE") === "true";
const SUPPORTS_DARK_MODE =
  getEnvironmentVariable("SUPPORTS_DARK_MODE") === "true";
const SUPPORTS_FOLLOW_ZIPCODES =
  getEnvironmentVariable("SUPPORTS_FOLLOW_ZIPCODES") === "true";
const STRIPE_CLIENT_ID = getEnvironmentVariable("STRIPE_CLIENT_ID");

const appMode = isAppMode();

var tabIndex = -1;
var tabIndexes = {};
var tabsArray = [];

if (!IS_SOLFORGE) {
  tabIndex++;
  tabIndexes["account"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Account",
    key: "account",
  });

  if (!IS_UY) {
    tabIndex++;
    tabIndexes["profile"] = tabIndex;
    tabsArray.push({
      index: tabIndex,
      label: "Profile",
      key: "profile",
    });
  }

  if (IS_BCP) {
    tabIndex++;
    tabIndexes["identities"] = tabIndex;
    tabsArray.push({
      index: tabIndex,
      label: "Identities",
      key: "identities",
    });
  }
}

// TODO: relocate back to between profile and teams
if (SUPPORTS_FOLLOW_ZIPCODES) {
  tabIndex++;
  tabIndexes["notifications"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Notifications",
    key: "notifications",
  });
}

// Removes tab for web but not app
if (!IS_UY && !(IS_BCP && !appMode)) {
  tabIndex++;
  tabIndexes["bank-details"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Bank Details",
    key: "bank-details",
  });
}

// Re-introduce organizations tab with re-direct logic to new /organize/dashboard page
if (!IS_BG || !appMode) {
  tabIndex++;
  tabIndexes["organizations"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Organizations",
    key: "organizations",
  });
}

if (STRIPE_CLIENT_ID) {
  if (!IS_UY) {
    tabIndex++;
    tabIndexes["bank-details"] = tabIndex;
    tabsArray.push({
      index: tabIndex,
      label: "Bank Details",
      key: "bank-details",
    });
  }
}

if (!NO_SUBSCRIPTIONS) {
  tabIndex++;
  tabIndexes["subscriptions"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Subscriptions",
    key: "subscriptions",
  });
}

if (SUPPORTS_DARK_MODE) {
  tabIndex++;
  tabIndexes["display"] = tabIndex;
  tabsArray.push({
    index: tabIndex,
    label: "Display",
    key: "display",
  });
}

export default function Account(params) {
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const useSignoutReducer = useSignout();
  const useProcessingReducer = useProcessing();

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(
    activeTabParam
      ? tabIndexes[activeTabParam.toLowerCase()] !== undefined
        ? tabIndexes[activeTabParam.toLowerCase()]
        : 0
      : 0
  );
  const handleTabChange = async (e, newValue) => {
    if (
      tabIndexes["organizations"] !== undefined &&
      newValue === tabIndexes["organizations"]
    ) {
      // Using window.location.href to re-render page with side panel, else it just navigates to new page
      window.location.href = "/organize/dashboard";
      return;
    }
    setLoading(true);
    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newValue) {
        path = newPath({
          active_tab: tabIndex,
        });

        return true;
      }
    });
    setActiveTab(newValue);
    navigate(path, { replace: appMode ? true : false });
    setLoading(false);
  };

  useEffect(() => {
    const go = async () => {
      try {
        var apiUser = await getData(
          `/users/${authContext?.authState?.identity?.userId}`,
          {
            queryStringParameters: {
              "expand[]": ["team", "bankAccount"],
            },
          }
        );

        if (apiUser) {
          if (!apiUser.socials) {
            apiUser.socials = {};
          } else if (Array.isArray(apiUser.socials)) {
            // handle legacy data
            var socials = {};
            apiUser.socials.forEach((social) => {
              socials[social.type] = social.username;
            });
            apiUser.socials = socials;
          }
          setUser(apiUser);

          setLoading(false);

          if (apiUser.profileFileId || apiUser.bannerFileId) {
            if (apiUser.profileFileId) {
              apiUser.profileFile = await getData(
                `/files/${apiUser.profileFileId}`,
                {
                  queryStringParameters: {},
                }
              );
            }
            if (apiUser.bannerFileId) {
              apiUser.bannerFile = await getData(
                `/files/${apiUser.bannerFileId}`,
                {
                  queryStringParameters: {},
                }
              );
            }

            setUser(apiUser);
          }
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          try {
            if (IS_BCP) {
              navigate("/logout");
            } else {
              await signout(useSignoutReducer, {
                setProcessing: useProcessingReducer,
                navigate: navigate,
              });
            }
          } catch (err) {
            postNotification("error", err);
          }
          navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
          return;
        } else {
          postNotification("error", err);
        }
      }
    };

    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            maxWidth: "100%",
            backgroundColor: "banner.main",
            color: "banner.contrastText",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Grid container alignItems="center">
              <Grid item xs={12} pr={[2, 0]} pl={[2, 0]}>
                <Typography variant="h3" gutterBottom>
                  {IS_SOLFORGE ? "Organized Play Settings" : "Account Settings"}
                </Typography>
              </Grid>
            </Grid>
            <Box
              sx={{
                width: "100%",
              }}
            >
              {tabsArray?.length > 0 && (
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="account tabs"
                  textColor="inherit"
                >
                  {tabsArray.map((tab) => (
                    <Tab
                      key={tab.key}
                      label={tab?.label}
                      {...tabPanelA11yProps(tab.index, tab.key)}
                    />
                  ))}
                </Tabs>
              )}
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ p: 2 }}>
          {tabsArray?.length > 0 &&
            user &&
            tabsArray.map((tab) => (
              <TabPanel key={tab} value={activeTab} index={tab.index}>
                {activeTab !== tab.index ? (
                  <></>
                ) : tab.key === "account" ? (
                  <AccountDetailsTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                    navigate={navigate}
                  />
                ) : tab.key === "identities" ? (
                  <IdentitiesTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                    navigate={navigate}
                  />
                ) : tab.key === "profile" ? (
                  <ProfileTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                  />
                ) : tab.key === "notifications" ? (
                  <NotificationsTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                  />
                ) : tab.key === "teams" ? (
                  <TeamsTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                  />
                ) : tab.key === "organizations" ? (
                  <OrganizationsTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                  />
                ) : tab.key === "bank-details" ? (
                  <BankDetailsTab user={user} authContext={authContext} />
                ) : tab.key === "subscriptions" ? (
                  <SubscriptionsTab
                    user={user}
                    setUser={setUser}
                    authContext={authContext}
                  />
                ) : tab.key === "bank-details" ? (
                  <BankDetailsTab user={user} authContext={authContext} />
                ) : tab.key === "display" ? (
                  <DisplayTab authContext={authContext} />
                ) : (
                  <></>
                )}
              </TabPanel>
            ))}
        </Container>
      </Box>
    </>
  );
}
